import { initSpnTablesApp } from "./spnTablesApp.js";
import { initSpnCompatibilitySummaryApp } from "./spnCompatibilitySummaryApp.js";
import { initSpnRpnCompatibilityStatusApp } from "./spnRpnCompatibilityStatusApp.js";
import { initSpnStudentCountApp } from "./spnStudentCountApp.js";
import { initRpnAdminEditorApp } from "./rpnAdminEditorApp.js";
import { initAdminReportsApp } from "./adminReportsApp.js";
import { initAdminSchoolRegistryApp } from "./adminSchoolRegistryApp.js";
import { initStudentIndividualAssignmentsApp } from "./studentIndividualAssignmentsApp";
import { initStudentGroupsApp } from "./studentGroupsApp";
import { initStudentManagerApp } from "./studentManagerApp";
import { initStudentIdentifierConversionApp } from "./studentIdentifierConversionApp";
import { initStudentRpnStatusApp } from "./studentRpnStatusApp";
import { initStudentStaleGroupAssignmentsApp } from "./studentStaleGroupAssignmentsApp";
import { initTeacherAssignmentsApp } from "./teacherAssignmentsApp";

const OBSERVED_ATTRIBUTE = "data-vue-lazy-observed";
const LOADING_ATTRIBUTE = "data-vue-lazy-loading";
const OBSERVER_ROOT_MARGIN = "200px 0px";

const registry = [
  {
    key: "student-identifier-conversion",
    selector: "[data-vue-student-identifier-conversion]",
    loadInit: () => initStudentIdentifierConversionApp,
  },
  {
    key: "student-stale-group-assignments",
    selector: "[data-vue-student-stale-group-assignments]",
    loadInit: () => initStudentStaleGroupAssignmentsApp,
  },
  {
    key: "student-individual-assignments",
    selector: "[data-vue-student-individual-assignments]",
    loadInit: () => initStudentIndividualAssignmentsApp,
  },
  {
    key: "student-groups",
    selector: "[data-vue-student-groups]",
    loadInit: () => initStudentGroupsApp,
  },
  {
    key: "spn-table-manager",
    selector: "[data-vue-spn-table-manager]",
    loadInit: () => initSpnTablesApp,
  },
  {
    key: "spn-compatibility-summary",
    selector: "[data-vue-spn-compatibility-summary]",
    loadInit: () => initSpnCompatibilitySummaryApp,
  },
  {
    key: "spn-rpn-compatibility-status",
    selector: "[data-vue-spn-rpn-compatibility-status]",
    loadInit: () => initSpnRpnCompatibilityStatusApp,
  },
  {
    key: "spn-student-count",
    selector: "[data-vue-spn-student-count]",
    loadInit: () => initSpnStudentCountApp,
  },
  {
    key: "rpn-admin-editor",
    selector: "[data-vue-rpn-admin-editor]",
    loadInit: () => initRpnAdminEditorApp,
  },
  {
    key: "admin-reports",
    selector: "[data-vue-admin-reports]",
    loadInit: () => initAdminReportsApp,
  },
  {
    key: "admin-school-registry",
    selector: "[data-vue-admin-school-registry]",
    loadInit: () => initAdminSchoolRegistryApp,
  },

  {
    key: "teacher-assignments",
    selector: "[data-vue-teacher-assignments]",
    loadInit: () => initTeacherAssignmentsApp,
  },
  {
    key: "student-manager",
    selector: "#student-manager",
    loadInit: () => initStudentManagerApp,
  },
  {
    key: "student-import-manager",
    selector: "#student-import-manager",
    loadInit: () => initStudentManagerApp,
  },
  {
    key: "student-rpn-status",
    selector: "[data-vue-student-rpn-status]",
    loadInit: () => initStudentRpnStatusApp,
  },
];

function collectElements(root, selector) {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) {
    elements.push(root);
  }

  if (root instanceof Document || root instanceof Element) {
    elements.push(...root.querySelectorAll(selector));
  }

  return Array.from(new Set(elements));
}

function supportsIntersectionObserver() {
  return typeof window !== "undefined" && "IntersectionObserver" in window;
}

function hasRenderableBox(element) {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

function isHiddenForLazyMount(element) {
  let current = element;

  while (current instanceof Element) {
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") {
      return true;
    }

    current = current.parentElement;
  }

  return false;
}

async function mountEntry(entry, root) {
  if (root instanceof Element) {
    if (root.hasAttribute("data-vue-mounted") || root.hasAttribute(LOADING_ATTRIBUTE)) {
      return null;
    }

    root.setAttribute(LOADING_ATTRIBUTE, entry.key);
    root.setAttribute("aria-busy", "true");
  }

  try {
    if (!entry.initPromise) {
      entry.initPromise = entry.loadInit();
    }

    const initApp = await entry.initPromise;
    const result = await initApp(root);
    return result;
  } catch (error) {
    entry.initPromise = null;

    if (root instanceof Element) {
      root.removeAttribute(OBSERVED_ATTRIBUTE);
      root.removeAttribute(LOADING_ATTRIBUTE);
      root.removeAttribute("aria-busy");
    }

    console.error(`Failed to lazy-mount Vue app "${entry.key}".`, error);
    return null;
  } finally {
    if (root instanceof Element) {
      root.removeAttribute(LOADING_ATTRIBUTE);
      if (root.hasAttribute("data-vue-mounted")) {
        root.removeAttribute("aria-busy");
      }
    }
  }
}

function getObserver(entry) {
  if (entry.observer || !supportsIntersectionObserver()) {
    return entry.observer ?? null;
  }

  entry.observer = new window.IntersectionObserver(
    (entries) => {
      entries.forEach((observerEntry) => {
        if (!observerEntry.isIntersecting) {
          return;
        }

        entry.observer?.unobserve(observerEntry.target);
        mountEntry(entry, observerEntry.target);
      });
    },
    {
      root: null,
      rootMargin: OBSERVER_ROOT_MARGIN,
      threshold: 0.01,
    },
  );

  return entry.observer;
}

export function initLazyVueApps(root = document, options = {}) {
  const eager = options?.eager === true;

  registry.forEach((entry) => {
    const elements = collectElements(root, entry.selector);

    elements.forEach((element) => {
      if (!(element instanceof Element)) {
        return;
      }

      if (element.hasAttribute("data-vue-mounted")) {
        return;
      }

      if (element.hasAttribute(LOADING_ATTRIBUTE)) {
        return;
      }

      const shouldMountImmediately =
        eager ||
        element.getAttribute("data-vue-eager") === "1" ||
        !supportsIntersectionObserver() ||
        (!hasRenderableBox(element) && !isHiddenForLazyMount(element));

      if (shouldMountImmediately) {
        mountEntry(entry, element);
        return;
      }

      if (element.hasAttribute(OBSERVED_ATTRIBUTE)) {
        return;
      }

      element.setAttribute(OBSERVED_ATTRIBUTE, entry.key);
      getObserver(entry)?.observe(element);
    });
  });
}
